// services
import base64ToFile from "@/services/base64-service";
import fileUploadService from "@/services/file-upload-service";
import axios from 'axios';

// utils
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import SNACKBAR_TYPES from "@/utils/enum/SNACKBAR_TYPES";
import { v4 as uuid } from 'uuid';
import store from "@/store";

const LEADING_ZEROS = 4;
/**
 * @param {String} type             type of the window. (contained in windowIdentifier - WindowFormModule)
 * @param {String|Number} id        id of the window. (contained in windowIdentifier - WindowFormModule)
 * @param {String} targetFieldPath  path where file gets stored.
 * @param {String} base64Strings
 * @param {String} taskPayload      additional stored information.
 * @param {Object} messages         messages for success and error
 * @returns {Promise<void>}
 */
export async function defaultUpload(type, id, targetFieldPath, base64Strings, taskPayload, messages) {
    const base64StringArray = base64Strings.split(',');
    var errors = 0;
    window.console.log(`Received ${base64StringArray?.length ?? (base64Strings ? 1 : 0)} files with intended name ${taskPayload?.fileName}`);
    let filename = undefined;

    if (base64StringArray === undefined || base64StringArray?.length <= 1) {
        if (taskPayload && taskPayload?.fileName) {
            let counter = String(taskPayload?.count + 1).padStart(LEADING_ZEROS, '0');
            filename = `${taskPayload?.fileName}-${counter}`;
        }
        let file = await base64ToFile(base64Strings, filename);
        try {
            postFile(type, id, targetFieldPath, file, taskPayload);
        } catch (e) {
            errors++;
        }
    }
    else {
        for (let [index, base64String] of base64StringArray.entries()) {
            if (taskPayload && taskPayload?.fileName) {
                let counter = String(taskPayload?.count + index + 1).padStart(LEADING_ZEROS, '0');
                filename = `${taskPayload?.fileName}-${counter}`;
            }
            let file = await base64ToFile(base64String, filename);
            try {
                postFile(type, id, targetFieldPath, file, taskPayload);
            } catch (e) {
                errors++;
            }
        }
    }

    if (errors > 0)
        showError(errors, messages.error);
    else {
        showSuccess(messages.success);
    }
}

/**
* @param {Object} fileAsApiObject
* @param {String|Number} fileAsApiObject.id
* @param {Object} fileAsApiObject.data
* @private
*/
function setFileIntoTargetField(type, id, targetFieldPath, fileAsApiObject) {
    if (targetFieldPath.startsWith('persistentData')) {
        store.commit(
            'activeWindows/setWindowDataField',
            { 
                type, 
                id, 
                targetFieldPath: `transientData.addInMemoryStoreAt.${uuid().toString()}`, 
                value: { targetFieldPath, value: fileAsApiObject}
            }
        );
    } else {
        store.commit(
            'activeWindows/setWindowDataField',
            { 
                type, 
                id, 
                targetFieldPath, 
                value: getValueForDataField(type, id, targetFieldPath, fileAsApiObject) 
            }
        );
    }
}

function createNewFile (response, taskPayload) {
    const baseData = {
        uuid: uuid().toString(),
        data: {
            file: { data: response.data },
            isDisplayedInPortal: false,
        },
    };

    if (taskPayload.projectId) {
        baseData.data.project = { id: taskPayload.projectId };
    }

    if (taskPayload.workflowActionId) {
        baseData.data.workflowAction = { id: taskPayload.workflowActionId };
    }

    if (taskPayload.workflowStepId) {
        baseData.data.workflowStep = { id: taskPayload.workflowStepId };
    }

    if (taskPayload.deviceId) {
        baseData.data.device = { id: taskPayload.deviceId };
    }

    return baseData;
};


async function postFile (type, id, targetFieldPath, file, taskPayload) {
    let response = await fileUploadService.postFile(file);
    if (taskPayload?.projectId && !taskPayload?.deviceId) {
        const newFile = createNewFile(response, taskPayload);
        await axios.post(
            `/rest/v4/projects/${taskPayload.projectId}/workflows/files`,
            [newFile]
        );
        store.commit('updateListeners/setWorkflowFilesUpdateRequired', true);
    } else if (taskPayload?.deviceId) {
        const newFile = createNewFile(response, taskPayload);
        setFileIntoTargetField(type, id, targetFieldPath, newFile);
    } else {
        const fileAsApiObject = fileUploadService.getNewFileForResponse(response.data, file);
        setFileIntoTargetField(type, id, targetFieldPath, fileAsApiObject);
    }
}

/**
* @param {Object} fileAsApiObject
* @returns {Array}
* @private
*/
function getValueForDataField(type, id, targetFieldPath, fileAsApiObject) {
    let windowData = store.getters['activeWindows/getWindowData']({ type, id });
    let targetField = cloneDeep(get(windowData, targetFieldPath));

    if (Array.isArray(targetField)) {
        targetField.push(fileAsApiObject);
    } else {
        targetField = [fileAsApiObject];
    }

    return targetField;
}

/**
* @private
*/
function showSuccess(message) {
    store.dispatch('globalSnackbarStore/showSnackbarWithMessage', {
        type: SNACKBAR_TYPES.SUCCESS,
        message: message
    });
}

/**
* @param {Number} errorNum
* @private
*/
function showError(errorNum, message) {
    store.dispatch('globalSnackbarStore/showSnackbarWithMessage', {
        type: SNACKBAR_TYPES.ERROR,
        message: message(errorNum)
    });
}